import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { Modal, Loader, Button } from 'ncoded-component-library';
import ChooseAFolderInDr from 'router/subrouters/DataRoom/components/ChooseAFolderInDr';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { FileTypeRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';

import './AddAllDocsToDataRoomModal.styles.scss';

type AddAllDocsToDataRoomModalProps = {
  className?: string;
  dataRoomRfiId: string;
  onAddAllSuccess: (files: FileTypeRFI[]) => void;
};

const AddAllDocsToDataRoomModal: ForwardRefRenderFunction<
  ModalRef,
  AddAllDocsToDataRoomModalProps
> = (props, ref) => {
  const { className, dataRoomRfiId, onAddAllSuccess } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [nodeToMoveTo, setNodeToMoveTo] = useState<string>(null);

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('add-all-docs-to-data-room-modal', className);

  const { t } = useTranslation();

  const onSubmit = useCallback(async () => {
    try {
      setIsLoading(true);

      const { data } = await dataRoomApi.rfi.bulkAddDRDocument(
        dataRoomRfiId,
        nodeToMoveTo,
      );

      onAddAllSuccess(data);
      modal.close();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [dataRoomRfiId, nodeToMoveTo, modal, onAddAllSuccess, popServerError]);

  return (
    <Modal ref={modalRef} className={classes} title={t('addAllFilesToDR')}>
      {isLoading && <Loader overlay={false} />}

      <p>{t('migrateNonDRFiles')}</p>

      <ChooseAFolderInDr
        dataRoomId={dataRoomId}
        currenntlySelectedNodeId={nodeToMoveTo}
        onChange={setNodeToMoveTo}
      />

      <div className="actions">
        <Button variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>
        <Button type="submit" onClick={onSubmit} disabled={isLoading}>
          {t('confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(AddAllDocsToDataRoomModal);
